import classNames from "classnames";
import { MobileFiltersContainer } from "./MobileFilters.styled";
import { LabelNavigation } from "src/molecules/LabelNavigation";
import { ArrowLeft } from "../AlgoliaProductList/MobileFilterView/DrawerFilter/DrawerFilter.styled";
import { useLockBodyScroll } from "src/hooks/useLockBodyScroll";
import { availableFiltersList } from "../AlgoliaProductList/availableFilters";
import { MobileFilterOption } from "./MobileFilterOption";
import { Trans } from "@lingui/macro";
import { ResultsButton } from "../AlgoliaProductList/DesktopFilterVeiw/DesktopFilter.styled";
import {
  useClearRefinements,
  useCurrentRefinements,
  useStats,
} from "react-instantsearch-core-v7";
import { MutableRefObject, useCallback, useMemo } from "react";
import { PLPTypes } from "src/types/PLPType.d";
import { PRICE_FACET } from "src/utils/algolia";
import { MobileFilterPriceOption } from "./MobileFilterPriceOption";
import { useRemoveFilters } from "src/hooks/use-handle-search-params";

type MobileFiltersProps = {
  isOpen: boolean;
  categoryPageId?: string;
  gender: string;
  pageContentType: PLPTypes;
  excludeFilters: string[];
  gridRef: MutableRefObject<HTMLDivElement | null>;
  toggleMobileFilters: () => void;
  trackFilterReset: () => void;
  handleScrollToTop: () => void;
};

export type FilterType =
  | "select"
  | "multi-select"
  | "multi-select-search"
  | "color"
  | "categories"
  | "range"
  | "brands";

function MobileFilters({
  isOpen,
  categoryPageId,
  gender,
  pageContentType,
  excludeFilters,
  gridRef,
  trackFilterReset,
  toggleMobileFilters,
  handleScrollToTop,
}: MobileFiltersProps): JSX.Element {
  useLockBodyScroll(isOpen);
  const { nbHits: total } = useStats();
  const { items } = useCurrentRefinements();
  const { refine: clear } = useClearRefinements();

  const hasFilters = useMemo(
    () => items.flatMap((item) => item.refinements).length > 0,
    [items]
  );

  const { removeAllFilters } = useRemoveFilters();

  const handleClearAll = useCallback(() => {
    clear();
    removeAllFilters(true);
    trackFilterReset();
  }, [clear, trackFilterReset, removeAllFilters]);

  return (
    <MobileFiltersContainer className={classNames({ ["open"]: isOpen })}>
      <LabelNavigation
        label={{ id: "Filters" }}
        leftAdornment={<ArrowLeft strokeWidth="2" />}
        onClick={toggleMobileFilters}
      />
      <div className="content-wrapper">
        {availableFiltersList
          .filter((item) => !excludeFilters.includes(item.facet))
          .map(({ facet, icon, name, type, isMultiColumn }) => {
            if (facet === PRICE_FACET) {
              return (
                <MobileFilterPriceOption
                  key={facet}
                  title={name}
                  icon={icon}
                  attribute={facet}
                  type={type as FilterType}
                  isMultiColumn={isMultiColumn || false}
                  categoryPageId={categoryPageId}
                  gender={gender}
                  handleScrollToTop={handleScrollToTop}
                />
              );
            }

            return (
              <MobileFilterOption
                key={facet}
                title={name}
                icon={icon}
                attribute={facet}
                type={type as FilterType}
                isMultiColumn={isMultiColumn || false}
                categoryPageId={categoryPageId}
                gender={gender}
                pageContentType={pageContentType}
                gridRef={gridRef}
                handleScrollToTop={handleScrollToTop}
              />
            );
          })}
      </div>

      <div className="close-button">
        {hasFilters ? (
          <ResultsButton fullWidth variant="tertiary" onClick={handleClearAll}>
            <Trans>Clear all</Trans>
          </ResultsButton>
        ) : null}

        <ResultsButton
          fullWidth
          variant="primary"
          onClick={toggleMobileFilters}
        >
          {hasFilters ? (
            <>
              <Trans>Show {total} results</Trans>
            </>
          ) : (
            <Trans>Close</Trans>
          )}
        </ResultsButton>
      </div>
    </MobileFiltersContainer>
  );
}

export { MobileFilters };
